// =====================================================================================
// PhaserRoomService - Manages Phaser room content updates
// =====================================================================================
// Extracted from useRoomConnection to separate Phaser update logic
// Handles world initialization, player synchronization, and room state updates

import type { Socket } from 'socket.io-client';
import type { Room, PlayerData } from '../game/types';
import type { HomeZoneClaimState } from '../types/room.types';
import type { PlayerUpdateType } from '../game/managers/RoomManager';
import { LeakDetector } from '../game/utils/LeakDetector';
import { getSystemsInCleanupOrder } from '../game/config/CleanupOrder';

// =====================================================================================
// TYPE DEFINITIONS
// =====================================================================================

interface PrivateAreaData {
  areaId: string;
  name?: string;
  isDoorOpen?: boolean;
  occupants?: Array<{
    userId: string;
    username: string;
  }>;
}

interface GameScene {
  roomManager?: {
    initializeWithSocket: (socket: Socket) => void;
    initializeLocalPlayer: (playerData: PlayerData, roomId: string) => void;
    updatePlayers: (players: PlayerData[], currentUserId: string, updateType: PlayerUpdateType) => void;
    handleTeleportUpdate: (userId: string, position: { x: number; y: number }) => void;
    isInitialized: boolean;
    socketId: string | null;
    cleanup: () => void;
  };
  initializeRoomWorld?: (roomId: string) => Promise<void>;
  initializePrivateAreaSystem?: (socket: Socket, userId: string) => void;
  updateZoneData?: (zones: HomeZoneClaimState[]) => void;
  updatePrivateAreas?: (areas: PrivateAreaData[]) => void;
  handlePrivateAreaUpdate?: (update: unknown) => void;
  /** Phaser's own scene event emitter, used to hand decorations to the scene. */
  events?: { emit: (event: string, ...args: unknown[]) => boolean };
  systems?: {
    player?: { cleanup: () => void };
    world?: { cleanup: () => void };
    privateAreas?: { cleanup: () => void };
  };
  registry?: {
    get: (key: string) => any;
    set: (key: string, value: any) => void;
    remove: (key: string) => void;
  };
  player?: any;
}

// =====================================================================================
// SERVICE CLASS
// =====================================================================================

/**
 * PhaserRoomService - Manages all Phaser-specific room operations
 * 
 * This service encapsulates all logic for updating Phaser game content
 * based on room state changes. It provides a clean separation between
 * WebSocket state management and game rendering.
 * 
 * Responsibilities:
 * - Room world initialization (tilemap loading)
 * - Player sprite management and synchronization
 * - Zone and private area visual updates
 * - Scene cleanup on room leave
 */
export class PhaserRoomService {
  
  /**
   * Initialize or update room manager with WebSocket connection
   * @param gameScene - The Phaser game scene
   * @param socket - Active WebSocket connection
   */
  public initializeRoomManager(gameScene: GameScene, socket: Socket | null): boolean {
    if (!gameScene.roomManager || !socket?.connected) {
      return false;
    }

    // Initialize or re-initialize if socket changed
    if (!gameScene.roomManager.isInitialized || 
        gameScene.roomManager.socketId !== socket.id) {
      gameScene.roomManager.initializeWithSocket(socket);
      return true;
    }

    return false;
  }

  /**
   * Initialize private area system with socket and user ID
   * @param gameScene - The Phaser game scene
   * @param socket - Active WebSocket connection
   * @param userId - Current user ID
   */
  public initializePrivateAreas(gameScene: GameScene, socket: Socket | null, userId: string): void {
    if (socket?.connected && gameScene.initializePrivateAreaSystem) {
      // Check if already initialized with same userId to prevent double initialization
      const currentUserId = gameScene.systems?.privateAreas?.localPlayerId;
      if (currentUserId === userId) {
        return;
      }
      gameScene.initializePrivateAreaSystem(socket, userId);
    }
  }

  /**
   * Update entire room content (world, players, zones)
   * @param gameScene - The Phaser game scene
   * @param room - Current room data
   * @param players - All players in the room
   * @param zones - Home zones in the room
   * @param privateAreas - Private areas in the room
   * @param userId - Current user ID
   * @param updateType - Type of player update (full or status-only)
   * @returns Promise that resolves when world is initialized
   */
  public async updateRoomContent(
    gameScene: GameScene,
    room: Room,
    players: PlayerData[],
    zones: HomeZoneClaimState[],
    privateAreas: PrivateAreaData[],
    userId: string,
    updateType: PlayerUpdateType = 'full'
  ): Promise<void> {
    if (!gameScene.roomManager) {
      console.warn('🎮 PhaserRoomService: No room manager available');
      return;
    }

    // Check if world needs initialization
    const isWorldInitialized = gameScene.registry?.get('worldInitialized');
    if (!isWorldInitialized) {
      await this.initializeWorld(gameScene, room.id);
    }

    // Initialize local player if needed
    const localPlayer = players.find(p => p.userId === userId);
    if (localPlayer) {
      this.initializeLocalPlayer(gameScene, localPlayer, room.id);
    }

    // Update all players
    this.updatePlayers(gameScene, players, userId, updateType);

    // Update zones
    if (zones.length > 0) {
      this.updateZones(gameScene, zones);
    }

    // Update private areas
    if (privateAreas.length > 0) {
      this.updatePrivateAreas(gameScene, privateAreas);
    }
  }

  /**
   * Initialize the room world (tilemap and collision layers)
   * @param gameScene - The Phaser game scene
   * @param roomId - ID of the room to initialize
   */
  private async initializeWorld(gameScene: GameScene, roomId: string): Promise<void> {
    console.info('🎮 PhaserRoomService: Initializing room world...');

    if (!gameScene.initializeRoomWorld) {
      console.warn('🎮 PhaserRoomService: initializeRoomWorld not available');
      return;
    }

    try {
      await gameScene.initializeRoomWorld(roomId);
      gameScene.registry?.set('worldInitialized', true);
      console.info('🎮 PhaserRoomService: World initialized successfully');
    } catch (error) {
      console.error('🎮 PhaserRoomService: Failed to initialize world:', error);
      throw error;
    }
  }

  /**
   * Initialize the local player sprite
   * @param gameScene - The Phaser game scene
   * @param playerData - Local player data
   * @param roomId - Current room ID
   */
  private initializeLocalPlayer(gameScene: GameScene, playerData: PlayerData, roomId: string): void {
    if (!gameScene.roomManager) {
      return;
    }

    // Check if we need to create/recreate the local player
    const existingPlayer = gameScene.registry?.get('player');
    const shouldCreatePlayer = !existingPlayer || 
                              !existingPlayer.scene ||  // Player was destroyed
                              existingPlayer.userId !== playerData.userId; // Different user

    if (shouldCreatePlayer) {
      gameScene.roomManager.initializeLocalPlayer(playerData, roomId);
    }
  }

  /**
   * Update all player sprites
   * @param gameScene - The Phaser game scene
   * @param players - All players in the room
   * @param userId - Current user ID
   * @param updateType - Type of update (full or status-only)
   */
  private updatePlayers(
    gameScene: GameScene, 
    players: PlayerData[], 
    userId: string, 
    updateType: PlayerUpdateType
  ): void {
    if (!gameScene.roomManager) {
      return;
    }

    gameScene.roomManager.updatePlayers(players, userId, updateType);
  }

  /**
   * Update zone visual states
   * @param gameScene - The Phaser game scene
   * @param zones - Home zones to update
   */
  private updateZones(gameScene: GameScene, zones: HomeZoneClaimState[]): void {
    if (gameScene.updateZoneData) {
      gameScene.updateZoneData(zones);
    }
  }

  /**
   * Update private area states
   * @param gameScene - The Phaser game scene
   * @param privateAreas - Private areas to update
   */
  private updatePrivateAreas(gameScene: GameScene, privateAreas: PrivateAreaData[]): void {
    if (gameScene.updatePrivateAreas) {
      gameScene.updatePrivateAreas(privateAreas);
    }
  }

  /**
   * Clean up scene content when leaving a room
   * @param gameScene - The Phaser game scene
   */
  public cleanupRoomContent(gameScene: GameScene): void {
    console.info('🎮 PhaserRoomService: Cleaning up room content');

    // Create a leak detector for development mode
    const leakDetector = import.meta.env.DEV ? new LeakDetector() : null;
    
    // Capture baseline before cleanup
    if (leakDetector) {
      leakDetector.captureBaseline(gameScene as any);
    }

    // Get all systems for cleanup
    const systems = gameScene.systems;
    
    if (systems) {
      // Use leak detector if available
      if (leakDetector && leakDetector.isEnabled()) {
        console.info('🧹 PhaserRoomService: Starting monitored cleanup...');
        
        // Get systems in the correct cleanup order
        const orderedSystems = getSystemsInCleanupOrder(systems);
        
        // Monitor cleanup of each system
        for (const { name, system } of orderedSystems) {
          try {
            leakDetector.monitorCleanup(
              name,
              gameScene as any,
              () => system.cleanup()
            );
          } catch (error) {
            console.error(`🎮 PhaserRoomService: Error cleaning up ${name}:`, error);
          }
        }
        
        // Handle room manager separately as it's not in systems
        if (gameScene.roomManager) {
          leakDetector.monitorCleanup(
            'roomManager',
            gameScene as any,
            () => gameScene.roomManager.cleanup()
          );
        }
        
        // Handle pathfinder separately since it might be null
        if (systems.pathfinder) {
          leakDetector.monitorCleanup(
            'pathfinder',
            gameScene as any,
            () => { systems.pathfinder = null; }
          );
        }
        
        // Log leak detection summary
        const summary = leakDetector.getSummary();
        if (summary.totalLeaked > 0) {
          console.error('🚨 Memory leaks detected during room cleanup:', summary);
        }
      } else {
        // Normal cleanup without monitoring - use the same cleanup order
        const orderedSystems = getSystemsInCleanupOrder(systems);
        
        for (const { name, system } of orderedSystems) {
          try {
            console.info(`🧹 Cleaning up ${name} system`);
            system.cleanup();
          } catch (error) {
            console.error(`🎮 PhaserRoomService: Error cleaning up ${name}:`, error);
          }
        }
        
        // Handle room manager separately as it's not in systems
        if (gameScene.roomManager) {
          try {
            console.info('🧹 Cleaning up room manager');
            gameScene.roomManager.cleanup();
          } catch (error) {
            console.error('🎮 PhaserRoomService: Error cleaning up room manager:', error);
          }
        }
        
        // Handle pathfinder separately since it might be null
        if (systems.pathfinder) {
          systems.pathfinder = null;
        }
      }
    }

    // DON'T clean up InputHandler - it's a persistent system that should work across rooms
    // Cleaning it up removes ALL input listeners including double-click movement
    
    // DON'T clean up AnimationManager - animation definitions are global, not room-specific

    // Clear registry
    if (gameScene.registry) {
      gameScene.registry.remove('player');
      gameScene.registry.remove('currentRoomId');
      gameScene.registry.remove('worldInitialized');
    }

    // Don't destroy player sprite here - PlayerSystem.cleanup() already handles it
    // This prevents double-destroy which can cause shadow darkening issues
    if (gameScene.player) {
      gameScene.player = null; // Just clear the reference
    }
  }

  /**
   * Check if the game scene is ready for updates
   * @param gameInstance - The Phaser game instance
   * @returns The game scene if ready, null otherwise
   */
  public getReadyGameScene(gameInstance: any): GameScene | null {
    if (!gameInstance) {
      return null;
    }

    try {
      const scene = gameInstance.scene.getScene('scene-game') as GameScene;
      if (scene?.roomManager) {
        return scene;
      }
    } catch (error) {
      console.warn('🎮 PhaserRoomService: Error accessing game scene:', error);
    }

    return null;
  }
}

// Export singleton instance
export const phaserRoomService = new PhaserRoomService();